import React, { useState } from 'react';
import { UserFeedback } from '../utils/faqApi';

interface FeedbackWidgetProps {
  requestId: string;
  interactionType: string;
  topic: string;
  onSubmit?: (feedback: UserFeedback) => void;
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  requestId,
  interactionType,
  topic,
  onSubmit
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const [showComment, setShowComment] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [ratingSubmitted, setRatingSubmitted] = useState<boolean>(false);

  const submitFeedback = (feedbackRating: number, feedbackComment?: string) => {
    const feedback: UserFeedback = {
      request_id: requestId,
      rating: feedbackRating,
      comment: feedbackComment?.trim() || undefined,
      timestamp: new Date().toISOString(),
      // user_id and organization_id would come from auth context when available
    };

    // Store feedback locally for now (can be sent to endpoint later)
    console.log('User Feedback:', feedback);

    // TODO: Send to feedback endpoint when available
    // if (feedbackEndpoint) {
    //   await fetch(feedbackEndpoint, {
    //     method: 'POST',
    //     headers: { 'Content-Type': 'application/json' },
    //     body: JSON.stringify(feedback)
    //   });
    // }

    if (onSubmit) {
      onSubmit(feedback);
    }
  };

  const handleRatingClick = (selectedRating: number) => {
    setRating(selectedRating);

    // Immediately save the rating
    submitFeedback(selectedRating);
    setRatingSubmitted(true);
  };

  const handleCommentSubmit = () => {
    // Re-submit with the comment
    submitFeedback(rating, comment);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 bg-green-50 border border-green-200 rounded text-xs text-green-700">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        Thank you for your feedback!
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 pt-2 mt-2">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-gray-600">Was this helpful?</span>
        {ratingSubmitted && !showComment && (
          <button
            onClick={() => setShowComment(true)}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
          >
            Add comment
          </button>
        )}
      </div>

      {/* Star Rating */}
      <div className="flex gap-1 items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => handleRatingClick(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="focus:outline-none transition-transform hover:scale-110"
            disabled={ratingSubmitted}
          >
            <svg
              className={`w-5 h-5 ${
                star <= (hoverRating || rating)
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              } ${ratingSubmitted ? 'cursor-default' : 'cursor-pointer'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </button>
        ))}
        {ratingSubmitted && (
          <span className="ml-2 text-xs text-green-600 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Saved
          </span>
        )}
      </div>

      {/* Optional Comment */}
      {showComment && (
        <div className="mt-2 space-y-1.5">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us more (optional)"
            rows={2}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
            maxLength={500}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">{comment.length}/500</span>
            <button
              onClick={handleCommentSubmit}
              className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
            >
              Submit Comment
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackWidget;
